import { useEffect, useMemo, useState } from "react";
import { History, Loader2, ChevronDown, Trash2, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PILLARS, getPillarByName } from "@/data/pillars";
import Markdown from "@/components/Markdown";

const WEEKLY_COLOR = "#8C8C8C";

function labelFor(r) {
  if (r.action === "week_review") return r.week_start ? `Weekly overview · week of ${fmtDate(r.week_start)}` : "Weekly overview";
  return r.pillar || "Reflection";
}

function colorFor(r) {
  if (r.action === "week_review") return WEEKLY_COLOR;
  return getPillarByName(r.pillar)?.color || WEEKLY_COLOR;
}

export default function ReflectionsTimeline() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/reflections");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const chips = useMemo(() => {
    if (!items) return [];
    const out = [{ key: "all", label: "All" }];
    if (items.some((r) => r.action === "week_review")) out.push({ key: "weekly", label: "Weekly", color: WEEKLY_COLOR });
    PILLARS.forEach((p) => {
      if (items.some((r) => r.pillar === p.name)) out.push({ key: p.name, label: p.name, color: p.color });
    });
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (filter === "all") return items;
    if (filter === "weekly") return items.filter((r) => r.action === "week_review");
    return items.filter((r) => r.pillar === filter);
  }, [items, filter]);

  const remove = async (id) => {
    try {
      await api.delete(`/ai/reflections/${id}`);
      setItems((it) => it.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-3xl px-6 md:px-12 py-10 md:py-14" data-testid="reflections-page">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Your journey</p>
      <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-2 flex items-center gap-3">
        <History className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} /> Reflections
      </h1>
      <p className="text-muted-foreground mb-8 max-w-2xl leading-relaxed">
        Every AI reflection you generate is kept here. Scroll back to watch how each pillar's tone and
        focus shift week over week.
      </p>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center" data-testid="reflections-empty">
          <Sparkles className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-foreground font-medium mb-1">No reflections yet</p>
          <p className="text-sm text-muted-foreground">
            Use "Analyse this week's progress" on the Dashboard, or "Coach me" on any pillar, to start your journey.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-8" data-testid="reflections-filters">
            {chips.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`reflections-filter-${c.key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors duration-200 ${
                  filter === c.key ? "bg-foreground text-background border-foreground" : "bg-white/70 text-foreground/80 border-input hover:border-foreground/40"
                }`}
              >
                {c.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />}
                {c.label}
              </button>
            ))}
          </div>

          <div className="relative pl-6">
            <span className="absolute left-[7px] top-1 bottom-1 w-px bg-border" aria-hidden />
            <div className="space-y-4">
              {filtered.map((r) => {
                const color = colorFor(r);
                const open = expandedId === r.id;
                return (
                  <div key={r.id} className="relative" data-testid={`reflection-${r.id}`}>
                    <span
                      className="absolute -left-[22px] top-4 w-3.5 h-3.5 rounded-full border-2 border-background"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    <div className="rounded-xl border border-border bg-card">
                      <button
                        onClick={() => setExpandedId(open ? null : r.id)}
                        className="w-full text-left px-4 py-3.5"
                        data-testid={`reflection-toggle-${r.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <Calendar className="w-3.5 h-3.5 text-muted-foreground" /> {fmtDate(r.created_at)}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 shrink-0 ${open ? "rotate-180" : ""}`} />
                        </div>
                        <p className="text-xs uppercase tracking-[0.12em] mt-1" style={{ color }}>{labelFor(r)}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {r.tone && (
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-secondary text-foreground/80" data-testid={`reflection-tone-${r.id}`}>
                              {r.tone}
                            </span>
                          )}
                          {(r.focus || []).map((f, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                              {f}
                            </span>
                          ))}
                        </div>
                      </button>
                      {open && (
                        <div className="px-4 pb-4 border-t border-border/60 pt-3">
                          <Markdown>{r.markdown}</Markdown>
                          <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/40">
                            <span className="text-[11px] text-muted-foreground">{r.provider} · {r.model}</span>
                            <button
                              onClick={() => remove(r.id)}
                              data-testid={`reflection-delete-${r.id}`}
                              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors duration-200"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
